<template>
  <div class="tokia-landing">
    <!-- Header y Navegación -->
    <v-app-bar app fixed color="white" elevation="2" height="70">
      <v-container
        class="d-flex align-center justify-space-between main-container"
      >
        <a href="#inicio" class="logo text-decoration-none d-flex align-center">
          <v-img :src="logo" max-height="64" max-width="150" contain></v-img>
        </a>

        <!-- <span class="primary--text text-h4 font-weight-bold d-none d-sm-block"
          >Tokia Play</span
        > -->

        <!-- Desktop Navigation -->
        <div class="d-none d-md-flex">
          <v-btn
            v-for="link in navLinks"
            :key="link.href"
            :href="link.href"
            text
            color="#F7941D"
            class="mx-2 font-weight-semibold"
            @click.prevent="scrollToSection(link.href)"
          >
            {{ link.text }}
          </v-btn>
          <v-btn
            href="#contacto"
            color="primary"
            class="font-weight-semibold"
            @click.prevent="scrollToSection('#contacto')"
          >
            Contacto
          </v-btn>
          <v-btn
            href="https://cloud.tokiaplay.com"
            color="secondary"
            class="font-weight-semibold ml-6"
            target="_blank"
            outlined
          >
            <v-icon left small>mdi-login</v-icon>
            Acceso Complejo
          </v-btn>
        </div>

        <!-- Mobile Menu Button -->
        <v-app-bar-nav-icon
          class="d-md-none"
          @click="mobileMenu = !mobileMenu"
        ></v-app-bar-nav-icon>
      </v-container>
    </v-app-bar>

    <!-- Mobile Navigation Drawer -->
    <v-navigation-drawer v-model="mobileMenu" app temporary right>
      <v-list>
        <v-list-item
          v-for="link in [...navLinks, { href: '#contacto', text: 'Contacto' }]"
          :key="link.href"
          :href="link.href"
          @click="scrollToSection(link.href)"
        >
          <v-list-item-content>
            <v-list-item-title
              style="color:#F7941D;"
              class="font-weight-semibold"
            >
              {{ link.text }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-divider class="my-2"></v-divider>

        <v-list-item href="https://cloud.tokiaplay.com" target="_blank">
          <v-list-item-icon>
            <v-icon color="secondary">mdi-login</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title
              style="color:#F7941D;"
              class="font-weight-semibold"
            >
              Acceso Complejo
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-main>
      <!-- Hero Section -->
      <section id="inicio" class="hero">
        <div class="hero-overlay"></div>
        <v-container class="text-center white--text py-16 hero-content">
          <h1 class="display-2 font-weight-bold mb-6 hero-title">
            Gestión Integral para Complejos Deportivos
          </h1>
          <p class="text-h6 mb-8 hero-text">
            Optimiza la administración de tu club con nuestra plataforma todo en
            uno. <span class="highlight">Tokia Play</span> te permite gestionar
            reservas, clases, torneos, ligas y mucho más desde un solo lugar.
          </p>
          <div class="hero-buttons">
            <v-btn
              x-large
              color="#f7941d"
              dark
              class="mr-4 mb-4 font-weight-bold hero-btn pulse-animation"
              elevation="8"
              @click="register"
            >
              <v-icon left>mdi-rocket-launch</v-icon>
              Registrate Ahora
            </v-btn>
            <v-btn
              x-large
              color="white"
              class="mb-4 font-weight-bold hero-btn"
              outlined
              dark
              elevation="8"
              @click="scrollToSection('#modulos')"
            >
              <v-icon left>mdi-eye</v-icon>
              Ver Módulos
            </v-btn>
          </div>
        </v-container>

        <!-- Decorative elements -->
        <div class="hero-decoration hero-decoration-1"></div>
        <div class="hero-decoration hero-decoration-2"></div>
        <div class="hero-decoration hero-decoration-3"></div>
      </section>

      <!-- Stats Section -->
      <section class="stats-section py-12">
        <v-container>
          <v-row>
            <v-col
              v-for="(stat, index) in stats"
              :key="index"
              cols="6"
              md="3"
              class="text-center"
            >
              <div class="stat-card">
                <v-icon size="48" color="primary" class="mb-3">{{
                  stat.icon
                }}</v-icon>
                <h3 class="display-1 font-weight-bold primary--text mb-2">
                  {{ stat.value }}
                </h3>
                <p class="text-body-1 grey--text text--darken-1">
                  {{ stat.label }}
                </p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <!-- Características principales -->
      <section id="caracteristicas" class="features py-16">
        <v-container>
          <h2 class="section-title caracteristicas">
            ¿Por qué elegir Tokia Play?
          </h2>
          <p
            class="text-center text-h6 grey--text text--darken-1 mb-12 subtitle-section"
          >
            Potenciamos tu gestión deportiva con tecnología de vanguardia
          </p>
          <v-row>
            <v-col
              v-for="(feature, index) in features"
              :key="feature.title"
              cols="12"
              md="3"
            >
              <v-card
                class="feature-card pa-8 text-center"
                elevation="4"
                :style="`animation-delay: ${index * 0.1}s`"
              >
                <div class="feature-icon-wrapper mb-4">
                  <v-icon size="64" color="primary" class="feature-icon-rotate">
                    {{ feature.icon }}
                  </v-icon>
                </div>
                <h3 class="text-h5 font-weight-bold primary--text mb-3">
                  {{ feature.title }}
                </h3>
                <p class="text-body-1 grey--text text--darken-1">
                  {{ feature.description }}
                </p>
                <div class="feature-card-accent"></div>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <!-- Módulos -->
      <section id="modulos" class="modules py-16">
        <v-container>
          <h2 class="section-title modulos-title">Módulos Especializados</h2>
          <p
            class="text-center text-h6 grey--text text--darken-1 mb-12 subtitle-section"
          >
            Cada herramienta diseñada para maximizar tu eficiencia
          </p>
          <v-row>
            <v-col
              v-for="(module, index) in modules"
              :key="module.title"
              cols="12"
              md="6"
              lg="4"
            >
              <v-card
                class="module-card"
                elevation="6"
                :style="`animation-delay: ${index * 0.05}s`"
              >
                <v-card-title class="module-header white--text">
                  <div class="module-header-content">
                    <v-icon color="white" class="mr-3 module-icon-pulse" large>
                      {{ module.icon }}
                    </v-icon>
                    <span class="text-h6 font-weight-bold">{{
                      module.title
                    }}</span>
                  </div>
                  <div class="module-header-bg"></div>
                </v-card-title>
                <v-card-text class="pa-6">
                  <p class="text-body-1 mb-4 grey--text text--darken-2">
                    {{ module.description }}
                  </p>
                  <v-list dense class="transparent">
                    <v-list-item
                      v-for="(item, idx) in module.features"
                      :key="idx"
                      class="px-0 module-feature-item"
                    >
                      <v-list-item-icon class="mr-3">
                        <v-icon color="primary" small>mdi-check-circle</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="text-body-2 feature-list">
                          {{ item }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <div class="module-card-shine"></div>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <!-- Precios -->
      <section id="precios" class="pricing py-16">
        <v-container>
          <h2 class="section-title pricing-title">Planes y Precios</h2>
          <p
            class="text-center text-h6 grey--text text--darken-1 mb-12 subtitle-section"
          >
            Elige el plan que mejor se adapte a tu complejo deportivo
          </p>
          <v-row justify="center">
            <v-col
              v-for="(plan, index) in pricingPlans"
              :key="index"
              cols="12"
              md="4"
            >
              <v-card
                class="pricing-card"
                :class="{ 'featured-plan': plan.featured }"
                elevation="8"
                :style="`animation-delay: ${index * 0.1}s`"
              >
                <div v-if="plan.featured" class="featured-badge">
                  <v-icon small class="mr-1">mdi-star</v-icon>
                  Más Popular
                </div>

                <v-card-text class="pa-8 text-center">
                  <div class="plan-icon-wrapper mb-4">
                    <v-icon
                      size="64"
                      :color="plan.featured ? 'secondary' : 'primary'"
                    >
                      {{ plan.icon }}
                    </v-icon>
                  </div>

                  <h3
                    class="text-h5 font-weight-bold mb-2"
                    :class="plan.featured ? 'secondary--text' : 'primary--text'"
                  >
                    {{ plan.name }}
                  </h3>

                  <p class="text-body-2 grey--text text--darken-1 mb-6">
                    {{ plan.description }}
                  </p>

                  <div class="price-wrapper mb-6">
                    <div v-if="plan.price" class="price-amount">
                      <span class="currency">$</span>
                      <span class="amount">{{ plan.price }}</span>
                      <span class="period">/mes</span>
                    </div>
                    <div v-else class="price-custom">
                      <span class="custom-text">Cotizar</span>
                    </div>
                  </div>

                  <v-divider class="mb-6"></v-divider>

                  <v-list class="transparent mb-6">
                    <v-list-item
                      v-for="(feature, idx) in plan.features"
                      :key="idx"
                      class="px-0 pricing-feature-item"
                    >
                      <v-list-item-icon class="mr-3">
                        <v-icon
                          :color="plan.featured ? 'secondary' : 'primary'"
                          small
                        >
                          mdi-check-circle
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="text-body-2">
                          {{ feature }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>

                  <v-btn
                    block
                    width="100"
                    :color="plan.featured ? 'secondary' : 'primary'"
                    class="font-weight-bold"
                    elevation="4"
                    @click="
                      plan.ctaAction === 'register'
                        ? register()
                        : scrollToSection('#contacto')
                    "
                  >
                    <v-icon left>{{ plan.ctaIcon }}</v-icon>
                    {{ plan.ctaText }}
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <div class="text-center mt-12">
            <v-alert
              color="#0B214B"
              dark
              prominent
              icon="mdi-information"
              class="pricing-info-alert"
              style="border-radius: 12px; max-width: 800px; margin: 0 auto;"
            >
              <div class="text-h6 mb-2">Todos los planes incluyen:</div>
              <div class="d-flex flex-wrap justify-center align-center gap">
                <v-chip class="ma-1" color="secondary" text-color="white">
                  <v-icon left small>mdi-check</v-icon>
                  Acceso completo a todos los módulos
                </v-chip>
                <v-chip class="ma-1" color="secondary" text-color="white">
                  <v-icon left small>mdi-check</v-icon>
                  Soporte técnico incluido
                </v-chip>
                <v-chip class="ma-1" color="secondary" text-color="white">
                  <v-icon left small>mdi-check</v-icon>
                  Actualizaciones gratuitas
                </v-chip>
                <v-chip class="ma-1" color="secondary" text-color="white">
                  <v-icon left small>mdi-check</v-icon>
                  Sin límite de usuarios
                </v-chip>
              </div>
            </v-alert>
          </div>
        </v-container>
      </section>

      <!-- Demo -->
      <section class="demo py-16 white--text text-center">
        <v-container>
          <h2 class="section-title white--text mb-6">Comienza Hoy Mismo</h2>
          <p class="text-h6 demo-text mb-8">
            Descubre cómo Tokia Play puede transformar la gestión de tu club
            deportivo. Regístrate ahora y empieza a optimizar tu gestión.
          </p>
          <v-btn
            x-large
            color="secondary"
            class="font-weight-bold pulse-animation"
            elevation="8"
            @click="register"
          >
            <v-icon left>mdi-rocket-launch</v-icon>
            Registrate Ahora
          </v-btn>
        </v-container>
      </section>

      <!-- Formulario de Consulta -->
      <section id="contacto" class="contact-form py-16">
        <v-container>
          <h2 class="section-title formulario mb-8">
            ¿Listo para optimizar tu club deportivo?
          </h2>
          <v-row justify="center">
            <v-col cols="12" md="8" lg="6">
              <v-card
                class="pa-6 contact-card"
                elevation="8"
                style="border-radius: 16px;"
              >
                <v-form
                  ref="contactForm"
                  v-model="formValid"
                  @submit.prevent="submitForm"
                >
                  <v-row>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="contactForm.apellido"
                        label="Apellido"
                        :rules="[rules.required]"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="contactForm.nombre"
                        label="Nombre"
                        :rules="[rules.required]"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="contactForm.nombreComplejo"
                        label="Nombre del Complejo"
                        :rules="[rules.required]"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="contactForm.email"
                        label="Email"
                        type="email"
                        :rules="[rules.required, rules.email]"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="contactForm.celular"
                        label="Celular"
                        :rules="[rules.required]"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        v-model="contactForm.consulta"
                        label="Tu consulta"
                        :rules="[rules.required]"
                        outlined
                        rows="5"
                      ></v-textarea>
                    </v-col>
                    <v-col cols="12" class="text-center">
                      <v-btn
                        type="submit"
                        x-large
                        color="primary"
                        :loading="formLoading"
                        :disabled="!formValid"
                        class="font-weight-bold px-8"
                        elevation="4"
                      >
                        <v-icon left>mdi-send</v-icon>
                        Enviar Consulta
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <!-- Footer -->
      <footer class="footer py-12">
        <v-container>
          <v-row>
            <v-col cols="12" md="4">
              <h3 class="text-h5 mb-4 font-weight-bold footer-title">
                Tokia Play
              </h3>
              <p class="text-body-2 footer-text footer-tokia">
                La solución integral para la gestión de complejos deportivos.
                Simplificamos la administración para que te enfoques en lo más
                importante: el deporte.
              </p>
            </v-col>
            <v-col cols="12" md="4">
              <h3 class="text-h5 mb-4 font-weight-bold footer-title">
                Módulos
              </h3>
              <v-list dense class="transparent">
                <v-list-item
                  v-for="module in modules.slice(0, 4)"
                  :key="module.title"
                  class="px-0"
                >
                  <v-list-item-content>
                    <v-list-item-title class="footer-link">
                      {{ module.title }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" md="4">
              <h3 class="text-h5 mb-4 font-weight-bold footer-title">
                Contacto
              </h3>
              <v-list dense class="transparent">
                <v-list-item class="px-0">
                  <v-list-item-icon class="mr-2">
                    <v-icon color="grey lighten-1" small>mdi-email</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="footer-text">
                      consultas@tokiaplay.com
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-icon class="mr-2">
                    <v-icon color="grey lighten-1" small>mdi-phone</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="footer-text">
                      +54 9 3547 65-2887
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-icon class="mr-2">
                    <v-icon color="grey lighten-1" small>mdi-map-marker</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="footer-text">
                      Córdoba, Argentina
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
          <v-divider class="my-6 grey darken-2"></v-divider>
          <div class="text-center">
            <p class="text-body-2 grey--text">
              &copy; {{ new Date().getFullYear() }} Tokia Play. Todos los
              derechos reservados.
            </p>
          </div>
        </v-container>
      </footer>
    </v-main>

    <!-- Snackbar para notificaciones -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import router from "../router/index.js";
import axios from "../axios-auth.js";
import logo from "@/assets/logo.webp";

export default {
  name: "TokiaPlayLanding",
  data() {
    return {
      logo,
      mobileMenu: false,
      formValid: false,
      formLoading: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      navLinks: [
        { href: "#inicio", text: "Inicio" },
        { href: "#caracteristicas", text: "Características" },
        { href: "#modulos", text: "Módulos" },
        { href: "#precios", text: "Precios" },
      ],
      stats: [
        { icon: "mdi-domain", value: "200+", label: "Complejos Activos" },
        { icon: "mdi-account-group", value: "50K+", label: "Usuarios Felices" },
        {
          icon: "mdi-calendar-check",
          value: "1M+",
          label: "Reservas Gestionadas",
        },
        { icon: "mdi-trophy", value: "5K+", label: "Torneos Organizados" },
      ],
      pricingPlans: [
        {
          name: "Plan Básico",
          description: "Ideal para complejos pequeños",
          icon: "mdi-home-city",
          price: "80.000",
          featured: false,
          features: ["De 1 a 4 canchas", "Todos las funcionalidades incluídas"],
          ctaText: "Comenzar Ahora",
          ctaIcon: "mdi-rocket-launch",
          ctaAction: "register",
        },
        {
          name: "Plan Profesional",
          description: "Perfecto para complejos medianos",
          icon: "mdi-office-building",
          price: "110.000",
          featured: true,
          features: ["De 5 a 8 canchas", "Todas las funcionalidades incluídas"],
          ctaText: "Comenzar Ahora",
          ctaIcon: "mdi-rocket-launch",
          ctaAction: "register",
        },
        {
          name: "Plan Enterprise",
          description: "Para grandes complejos deportivos",
          icon: "mdi-city",
          price: null,
          featured: false,
          features: ["Más de 8 canchas", "Todas las funcionalidades incluídas"],
          ctaText: "Pedir Cotización",
          ctaIcon: "mdi-email",
          ctaAction: "contact",
        },
      ],
      features: [
        {
          icon: "mdi-cog",
          title: "Todo en una Plataforma",
          description:
            "Gestiona todas las áreas de tu club deportivo desde un único sistema integrado, eliminando la necesidad de múltiples herramientas.",
        },
        {
          icon: "mdi-chart-line",
          title: "Automatización Inteligente",
          description:
            "Automatiza procesos como reservas, cobros y liquidaciones, reduciendo errores y ahorrando tiempo valioso.",
        },
        {
          icon: "mdi-cellphone",
          title: "Acceso en Cualquier Lugar",
          description:
            "Plataforma responsive que funciona en cualquier dispositivo, permitiendo la gestión desde la oficina o en movimiento.",
        },
        {
          icon: "mdi-face-agent",
          title: "Soporte 24hs.",
          description:
            "Alguien de nuestro equipo estará siempre a disposición para guiarlo y atender sus consultas.",
        },
      ],
      modules: [
        {
          icon: "mdi-calendar-check",
          title: "Gestión de Turnos y Cobros",
          description:
            "Sistema completo para la reserva de turnos y gestión de cobros asociados a cada consumo.",
          features: [
            "Gestión de turnos en el complejo y en el portal del jugador",
            "Gestión de cronogramas flexible de canchas",
            "Copie y reprograme turnos de manera muy fácil",
            "Sistema de gestión de consumos y cobros del turno",
            "Recordatorios automáticos",
          ],
        },
        {
          icon: "mdi-teach",
          title: "Clases y Honorarios",
          description:
            "Administra clases deportivas y automatiza la liquidación de honorarios a profesores.",
          features: [
            "Gestión de profesores",
            "Programación de clases",
            "Control de asistencia",
            "Liquidación de honorarios a profesores",
            "Reportes de rendimiento",
          ],
        },
        {
          icon: "mdi-soccer-field",
          title: "Gestión de Canchas y Horarios",
          description:
            "Optimiza el uso de tus instalaciones con un sistema inteligente de gestión de canchas y horarios.",
          features: [
            "Visualización de disponibilidad",
            "Bloqueo de canchas por mantenimiento",
            "Tarifas variables por horario",
            "Calendario integrado con gestión de feriados",
            "Tipos de turno y horarios flexibles",
          ],
        },
        {
          icon: "mdi-trophy",
          title: "Torneos y Ligas",
          description:
            "Organiza y gestiona torneos y ligas de manera sencilla, con seguimiento completo de cada competencia.",
          features: [
            "Creación y configuración de torneos",
            "Creación y configuración de ligas",
            "Gestión de inscripciones",
            "Generación de fixtures",
            "Visualización de horarios, lugar y avances de la competición en el portal del jugador",
          ],
        },
        {
          icon: "mdi-account-group",
          title: "Jugadores y Ranking",
          description:
            "Administra jugadores y mantén un ranking actualizado por categoría para motivar la competencia.",
          features: [
            "Base de datos de jugadores con sus datos completos",
            "Gestión de características del jugador",
            "Sistema de ranking por categoría",
            "Historial de rendimiento",
            "Portal del jugador propio",
          ],
        },
        {
          icon: "mdi-monitor",
          title: "Portal del Jugador",
          description:
            "Ofrece a tus jugadores un portal personalizado para visualizar torneos, ligas, reservas y su historial.",
          features: [
            "Acceso a torneos y ligas activas",
            "Realización de reservas online",
            "Cancelaciones de turnos",
            "Visualización de ranking por deporte, año, género y categoría",
            "Notificacion de datos del jugador",
          ],
        },
      ],
      contactForm: {
        apellido: "",
        nombre: "",
        nombreComplejo: "",
        email: "",
        celular: "",
        consulta: "",
      },
      rules: {
        required: (value) => !!value || "Este campo es requerido",
        email: (value) => {
          const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return pattern.test(value) || "Email inválido";
        },
      },
    };
  },
  methods: {
    scrollToSection(href) {
      this.mobileMenu = false;
      const targetId = href.substring(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        const offsetTop = targetElement.offsetTop - 80;
        window.scrollTo({
          top: offsetTop,
          behavior: "smooth",
        });
      }
    },
    async submitForm() {
      if (this.$refs.contactForm.validate()) {
        this.formLoading = true;
        try {
          // Aquí puedes emitir un evento para que el componente padre maneje el envío
          this.$emit("submit-contact", this.contactForm);

          // O hacer la petición directamente aquí
          // await this.$axios.post('/api/contact', this.contactForm);
          await axios.get("/Institucional/GuardarConsulta", {
            params: {
              nombre: this.contactForm.nombre,
              apellido: this.contactForm.apellido,
              asunto: this.contactForm.nombreComplejo,
              mail: this.contactForm.email,
              celular: this.contactForm.celular,
              texto: this.contactForm.consulta,
              idLanding: 7,
            },
          });

          this.showSnackbar("¡Consulta enviada exitosamente!", "success");
          this.resetForm();
        } catch (error) {
          this.showSnackbar(
            "Error al enviar la consulta. Intenta nuevamente.",
            "error",
          );
        } finally {
          this.formLoading = false;
        }
      }
    },
    resetForm() {
      this.$refs.contactForm.reset();
      this.contactForm = {
        apellido: "",
        nombre: "",
        nombreComplejo: "",
        email: "",
        celular: "",
        consulta: "",
      };
    },
    showSnackbar(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    register() {
      router.push("/RegistrarUsuario");
    },
  },
};
</script>

<style scoped>
.tokia-landing {
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.main-container {
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  max-width: 100%;
}

/* Animaciones */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

@keyframes shine {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Hero Section */
.hero {
  background: linear-gradient(
      135deg,
      rgba(11, 33, 75, 0.95) 0%,
      rgba(11, 33, 75, 0.85) 100%
    ),
    url("~@/assets/hero.webp");
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  min-height: 700px;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(
    ellipse at center,
    rgba(247, 148, 29, 0.1) 0%,
    transparent 70%
  );
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 2;
}

.hero-title {
  animation: fadeInUp 0.8s ease-out;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}

.hero-text {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.95;
  animation: fadeInUp 1s ease-out 0.2s backwards;
}

.highlight {
  color: #f7941d;
  font-weight: 700;
  text-shadow: 0 0 20px rgba(247, 148, 29, 0.5);
}

.hero-buttons {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  animation: fadeInUp 1.2s ease-out 0.4s backwards;
}

.hero-btn {
  transition: all 0.3s ease;
  text-transform: none;
  font-size: 1.1rem;
  padding: 0 32px !important;
}

.hero-btn:hover {
  transform: translateY(-3px);
}

.pulse-animation {
  animation: pulse 2s ease-in-out infinite;
}

/* Decorative elements */
.hero-decoration {
  position: absolute;
  border-radius: 50%;
  opacity: 0.1;
  animation: float 6s ease-in-out infinite;
}

.hero-decoration-1 {
  width: 300px;
  height: 300px;
  background: #f7941d;
  top: 10%;
  left: 5%;
  animation-delay: 0s;
}

.hero-decoration-2 {
  width: 200px;
  height: 200px;
  background: #ffffff;
  bottom: 15%;
  right: 10%;
  animation-delay: 1s;
}

.hero-decoration-3 {
  width: 150px;
  height: 150px;
  background: #f7941d;
  top: 60%;
  left: 80%;
  animation-delay: 2s;
}

/* Stats Section */
.stats-section {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-bottom: 1px solid #e0e0e0;
}

.stat-card {
  padding: 2rem 1rem;
  transition: all 0.3s ease;
  border-radius: 12px;
}

.stat-card:hover {
  transform: translateY(-5px);
}

/* Sections */
.section-title {
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 1rem;
  font-weight: 700;
  position: relative;
  padding-bottom: 1.5rem;
}

.section-title::after {
  content: "";
  position: absolute;
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #f7941d 0%, #ff9800 100%);
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 2px;
  box-shadow: 0 2px 4px rgba(247, 148, 29, 0.3);
}

.caracteristicas {
  color: #0b214b;
}

.modulos-title {
  color: #0b214b;
}

.formulario {
  color: #424242;
}

.subtitle-section {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

/* Pricing Section */
.pricing {
  background: linear-gradient(135deg, #f8f9fa 0%, #e8eaf6 100%);
}

.pricing-title {
  color: #0b214b;
}

.pricing-card {
  height: 100%;
  border-radius: 16px;
  transition: all 0.4s ease;
  position: relative;
  overflow: visible;
  animation: fadeInUp 0.6s ease-out backwards;
}

.pricing-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(11, 33, 75, 0.2) !important;
}

.featured-plan {
  border: 3px solid #f7941d;
  transform: scale(1.05);
}

.featured-plan:hover {
  transform: translateY(-10px) scale(1.05);
}

.featured-badge {
  position: absolute;
  top: -12px;
  right: 20px;
  background: linear-gradient(135deg, #f7941d 0%, #ff9800 100%);
  color: white;
  padding: 6px 20px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(247, 148, 29, 0.4);
  display: flex;
  align-items: center;
}

.plan-icon-wrapper {
  background: linear-gradient(
    135deg,
    rgba(11, 33, 75, 0.05) 0%,
    rgba(247, 148, 29, 0.05) 100%
  );
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  transition: all 0.3s ease;
}

.pricing-card:hover .plan-icon-wrapper {
  transform: scale(1.1) rotate(5deg);
}

.featured-plan .plan-icon-wrapper {
  background: linear-gradient(
    135deg,
    rgba(247, 148, 29, 0.1) 0%,
    rgba(255, 152, 0, 0.1) 100%
  );
}

.price-wrapper {
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.price-amount {
  display: flex;
  align-items: baseline;
  justify-content: center;
}

.currency {
  font-size: 1.5rem;
  font-weight: 600;
  color: #0b214b;
  margin-right: 4px;
}

.amount {
  font-size: 3rem;
  font-weight: 700;
  color: #0b214b;
  line-height: 1 !important;
}

.period {
  font-size: 1rem;
  color: #666;
  margin-left: 4px;
}

.price-custom {
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-text {
  font-size: 2.5rem;
  font-weight: 700;
  color: #0b214b;
  background: linear-gradient(135deg, #0b214b 0%, #f7941d 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1 !important;
}

.pricing-feature-item {
  transition: all 0.2s ease;
  border-radius: 8px;
  padding: 8px 0;
}

.pricing-feature-item:hover {
  background-color: rgba(11, 33, 75, 0.02);
  padding-left: 8px;
}

.pricing-info-alert {
  animation: fadeInUp 0.8s ease-out 0.4s backwards;
}

.gap {
  gap: 8px;
}

/* Features */
.features {
  background: linear-gradient(135deg, #f8f9fa 0%, #e8eaf6 100%);
}

.feature-list {
  line-height: 1.5 !important;
  white-space: normal;
}

.feature-card {
  height: 100%;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  background: white;
  animation: fadeInUp 0.6s ease-out backwards;
}

.feature-card:hover {
  transform: translateY(-15px) scale(1.02);
  box-shadow: 0 20px 40px rgba(11, 33, 75, 0.2) !important;
}

.feature-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #0b214b 0%, #f7941d 100%);
  transform: scaleX(0);
  transition: transform 0.4s ease;
}

.feature-card:hover::before {
  transform: scaleX(1);
}

.feature-icon-wrapper {
  background: linear-gradient(
    135deg,
    rgba(11, 33, 75, 0.05) 0%,
    rgba(247, 148, 29, 0.05) 100%
  );
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  transition: all 0.3s ease;
}

.feature-card:hover .feature-icon-wrapper {
  transform: scale(1.1);
  background: linear-gradient(
    135deg,
    rgba(11, 33, 75, 0.1) 0%,
    rgba(247, 148, 29, 0.1) 100%
  );
}

.feature-icon-rotate {
  transition: all 0.5s ease;
}

.feature-card:hover .feature-icon-rotate {
  transform: rotateY(360deg);
}

.feature-card-accent {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, #f7941d 0%, transparent 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.feature-card:hover .feature-card-accent {
  opacity: 1;
}

/* Modules */
.modules {
  background: linear-gradient(135deg, #ffffff 0%, #f5f7fa 100%);
}

.module-card {
  height: 100%;
  border-radius: 16px;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  animation: fadeInUp 0.6s ease-out backwards;
}

.module-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(11, 33, 75, 0.15) !important;
}

.module-header {
  background: linear-gradient(135deg, #0b214b 0%, #1a3a6b 100%) !important;
  padding: 24px !important;
  position: relative;
  overflow: hidden;
}

.module-header-content {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  width: 100%;
}

.module-header-bg {
  position: absolute;
  top: 0;
  right: -50px;
  width: 150px;
  height: 150px;
  background: radial-gradient(
    circle,
    rgba(247, 148, 29, 0.2) 0%,
    transparent 70%
  );
  border-radius: 50%;
  transition: all 0.4s ease;
}

.module-card:hover .module-header-bg {
  transform: scale(1.5);
  opacity: 0.3;
}

.module-icon-pulse {
  animation: pulse 2s ease-in-out infinite;
}

.module-feature-item {
  transition: all 0.2s ease;
  border-radius: 8px;
  padding: 4px 0;
}

.module-feature-item:hover {
  background-color: rgba(11, 33, 75, 0.02);
  padding-left: 8px;
}

.module-card-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  transition: left 0.5s ease;
}

.module-card:hover .module-card-shine {
  left: 100%;
}

/* Demo Section */
.demo {
  background: linear-gradient(135deg, #0b214b 0%, #1a3a6b 100%);
  position: relative;
  overflow: hidden;
}

.demo::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle,
    rgba(247, 148, 29, 0.1) 0%,
    transparent 50%
  );
  animation: rotate 30s linear infinite;
}

.demo h2 {
  color: white;
  position: relative;
  z-index: 2;
}

.demo h2::after {
  background: linear-gradient(90deg, #f7941d 0%, #ff9800 100%);
}

.demo-text {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.95;
  position: relative;
  z-index: 2;
}

/* Contact Form */
.contact-form {
  background: linear-gradient(135deg, #f8f9fa 0%, #e8eaf6 100%);
}

.contact-card {
  transition: all 0.3s ease;
}

.contact-card:hover {
  box-shadow: 0 12px 24px rgba(11, 33, 75, 0.15) !important;
}

/* Footer */
.footer {
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  color: white;
}

.footer-title {
  color: #f7941d !important;
}

.footer-text {
  color: #ccc !important;
}

.footer-link {
  color: #ccc !important;
  cursor: pointer;
  transition: all 0.3s ease;
}

.footer-link:hover {
  color: #f7941d !important;
  padding-left: 5px;
}

.footer-tokia {
  padding-top: 1.2rem;
}

/* Responsive */
@media (max-width: 959px) {
  .hero {
    min-height: 600px;
  }

  .hero-title {
    font-size: 2.2rem !important;
  }

  .hero-buttons {
    flex-direction: column;
    align-items: center;
  }

  .section-title {
    font-size: 2rem;
  }

  .hero-decoration {
    display: none;
  }
}

@media (max-width: 599px) {
  .hero {
    min-height: 500px;
    background-attachment: scroll;
  }

  .hero-title {
    font-size: 1.8rem !important;
  }

  .section-title {
    font-size: 1.75rem;
  }

  .stat-card {
    padding: 1rem 0.5rem;
  }

  .primary--text.text-h4 {
    font-size: 1.5rem !important;
  }
}
</style>
